import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for handling action hotkeys in dashboard components
 * @param {Object} options - Configuration options
 * @param {Array} options.actions - Array of action objects with label and onClick
 * @param {string} options.activeMainTab - Current main tab
 * @param {string} options.activeSubTab - Current sub tab
 * @param {boolean} options.disabled - Whether hotkeys should be disabled
 * @returns {void}
 */
const useActionHotkeys = ({ actions, activeMainTab, activeSubTab, disabled = false }) => {
   const handleKeyPress = useCallback((e) => {
      // Only handle if not disabled and not typing in an input/textarea
      if (disabled || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
         return;
      }

      // Handle Ctrl + Number keys (1-9)
      if (e.ctrlKey && !e.altKey && !e.shiftKey) {
         const keyNumber = parseInt(e.key);

         // Check if key is a number and within range of available actions
         if (!isNaN(keyNumber) && keyNumber > 0 && keyNumber <= actions.length) {
            e.preventDefault();

            const action = actions[keyNumber - 1];
            if (action?.onClick) {
               action.onClick();
               // Show toast notification for the executed action
               toast.info(`Executed: ${action.label}`);
            }
         }
      }
   }, [actions, disabled]);

   useEffect(() => {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [handleKeyPress]);
};

export default useActionHotkeys;